import axios from "axios";
import { APP_VERSION, getBackendUrl } from "../config";

type ClientErrorPayload = {
  company_id?: number;
  user_id?: number | null;
  username?: string | null;
  route?: string;
  message: string;
  error_name?: string;
  stack?: string;
  component_stack?: string;
  source_file?: string;
  line_no?: number;
  column_no?: number;
  event_type?: string;
  severity?: "DEBUG" | "INFO" | "WARN" | "ERROR" | "CRITICAL";
  build_version?: string;
  client_info?: Record<string, any>;
};

declare global {
  interface Window {
    __aviraaproClientErrorHandlers?: boolean;
  }
}

const ENDPOINT = "/api/admin/monitor/client-errors";
const DEDUPE_MS = 30_000;
const recentKeys = new Map<string, number>();

function clip(value: any, limit: number): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value);
  return text.length > limit ? text.slice(0, limit) : text;
}

function getStoredInt(key: string): number | null {
  try {
    const value = localStorage.getItem(key);
    if (!value || !/^\d+$/.test(value)) return null;
    return Number(value);
  } catch {
    return null;
  }
}

function getStoredString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function shouldSend(payload: ClientErrorPayload): boolean {
  const key = `${payload.event_type || "client_error"}|${payload.route || ""}|${payload.message}`.slice(0, 600);
  const now = Date.now();
  const last = recentKeys.get(key) || 0;
  if (now - last < DEDUPE_MS) return false;
  recentKeys.set(key, now);

  for (const [k, ts] of Array.from(recentKeys.entries())) {
    if (now - ts > DEDUPE_MS * 4) recentKeys.delete(k);
  }
  return true;
}

function endpointUrl(): string {
  const base = getBackendUrl();
  return base ? `${base}${ENDPOINT}` : ENDPOINT;
}

export function reportClientError(input: ClientErrorPayload): void {
  const storedCompanyId = input.company_id || getStoredInt("company_id") || getStoredInt("erp_company_id") || undefined;
  const payload: ClientErrorPayload = {
    company_id: storedCompanyId,
    user_id: input.user_id ?? getStoredInt("user_id"),
    username: input.username ?? getStoredString("username"),
    route: input.route || `${window.location.pathname}${window.location.search}`,
    message: clip(input.message || "Client error", 2000) || "Client error",
    error_name: clip(input.error_name, 160),
    stack: clip(input.stack, 6000),
    component_stack: clip(input.component_stack, 6000),
    source_file: clip(input.source_file, 500),
    line_no: input.line_no,
    column_no: input.column_no,
    event_type: clip(input.event_type || "client_error", 40),
    severity: input.severity || "ERROR",
    build_version: clip(input.build_version || APP_VERSION, 80),
    client_info: {
      url: window.location.href,
      online: navigator.onLine,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...input.client_info,
    },
  };

  if (!shouldSend(payload)) return;

  const token = getStoredString("token") || getStoredString("erp_token");
  const tenantId = getStoredString("tenant_id") || getStoredString("erp_tenant_id");
  const companyId = getStoredString("company_id") || getStoredString("erp_company_id");
  axios.post(endpointUrl(), payload, {
    timeout: 8000,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { "X-Tenant-ID": tenantId } : {}),
      ...(companyId ? { "X-Company-ID": companyId } : {}),
    },
  }).catch(() => {
    // Error reporting must never disturb normal ERP work.
  });
}

export function reportApiError(error: any, context: Record<string, any> = {}): void {
  const status = Number(error?.response?.status || 0);
  const method = String(error?.config?.method || "GET").toUpperCase();
  const url = String(error?.config?.url || "");
  const baseURL = String(error?.config?.baseURL || "");

  if (status > 0 && status < 500) return;
  if (url.includes(ENDPOINT)) return;

  reportClientError({
    event_type: status ? "api_5xx" : "api_network",
    severity: status >= 500 ? "ERROR" : "WARN",
    message: error?.response?.data?.detail || error?.message || "API request failed",
    error_name: error?.name || "AxiosError",
    route: `${window.location.pathname}${window.location.search}`,
    client_info: {
      status_code: status || null,
      method,
      endpoint: `${baseURL}${url}`,
      ...context,
    },
  });
}

export function installGlobalErrorHandlers(): void {
  if (window.__aviraaproClientErrorHandlers) return;
  window.__aviraaproClientErrorHandlers = true;

  window.addEventListener("error", (event) => {
    reportClientError({
      event_type: "window_error",
      severity: "ERROR",
      message: event.message || "Unhandled browser error",
      error_name: event.error?.name || "Error",
      stack: event.error?.stack,
      source_file: event.filename,
      line_no: event.lineno,
      column_no: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    reportClientError({
      event_type: "unhandled_rejection",
      severity: "ERROR",
      message: reason?.message || String(reason || "Unhandled promise rejection"),
      error_name: reason?.name || "UnhandledRejection",
      stack: reason?.stack,
    });
  });
}
